import type { ComplianceMetrics } from './types';
import { Card, CardContent } from '@/components/ui/card';
import { ShieldCheck, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface Props {
  compliance: ComplianceMetrics | null;
  loading: boolean;
}

function getColor(score: number) {
  if (score >= 80) return { text: 'text-[hsl(var(--status-ok))]', ring: 'ring-[hsl(var(--status-ok)/0.3)]', bar: 'hsl(152, 69%, 36%)' };
  if (score >= 60) return { text: 'text-[hsl(var(--status-warning))]', ring: 'ring-[hsl(var(--status-warning)/0.3)]', bar: 'hsl(38, 80%, 45%)' };
  return { text: 'text-[hsl(var(--status-error))]', ring: 'ring-[hsl(var(--status-error)/0.3)]', bar: 'hsl(0, 72%, 50%)' };
}

function TrendIcon({ diff }: { diff: number }) {
  if (diff > 0) return <span className="inline-flex items-center gap-0.5 text-[hsl(var(--status-ok))] text-[10px] font-medium"><TrendingUp className="h-3 w-3" />+{diff}</span>;
  if (diff < 0) return <span className="inline-flex items-center gap-0.5 text-[hsl(var(--status-error))] text-[10px] font-medium"><TrendingDown className="h-3 w-3" />{diff}</span>;
  return <span className="inline-flex items-center gap-0.5 text-muted-foreground text-[10px] font-medium"><Minus className="h-3 w-3" />±0</span>;
}

const dimensions = [
  { key: 'curation' as const, label: 'Curation', desc: 'Anteil kuratierter Assets über alle Tools' },
  { key: 'stewardship' as const, label: 'Stewardship', desc: 'Assets mit zugewiesenen Verantwortlichen' },
  { key: 'approvals' as const, label: 'Approvals', desc: 'Genehmigte vs. offene Review-Anfragen' },
  { key: 'auditCoverage' as const, label: 'Audit-Abdeckung', desc: 'Tools mit aktivem Audit-Logging' },
] as const;

export function ComplianceScorecard({ compliance, loading }: Props) {
  if (loading) {
    return (
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">Compliance-Scorecard</h3>
        <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-3">
          <div className="h-36 animate-pulse rounded-lg bg-muted" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-36 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!compliance) return null;

  const overallColor = getColor(compliance.overall);

  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground mb-3">Compliance-Scorecard</h3>
      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-3">
        {/* Overall score */}
        <Card className={`border-border ring-1 ${overallColor.ring}`}>
          <CardContent className="flex flex-col items-center justify-center p-4 h-full">
            <ShieldCheck className={`h-6 w-6 ${overallColor.text} mb-1`} />
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Gesamt-Score</p>
            <p className={`text-3xl font-bold ${overallColor.text} leading-tight`}>{compliance.overall}</p>
            <TrendIcon diff={0} />
            <p className="text-[10px] text-muted-foreground mt-1">vs. Vorwoche</p>
          </CardContent>
        </Card>

        {/* Dimensions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {dimensions.map(({ key, label, desc }) => {
            const score = compliance[key].score;
            const c = getColor(score);
            return (
              <Card key={key} className="border-border">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-medium text-foreground">{label}</p>
                    <TrendIcon diff={0} />
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-xl font-bold ${c.text}`}>{score}</span>
                    <span className="text-[10px] text-muted-foreground">/100</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-muted mt-2">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${score}%`, backgroundColor: c.bar }}
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5 leading-snug">{desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
